import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errText, API } from "@/lib/api";
import { useAuth, getToken } from "@/lib/auth";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, FileText, MessageSquare, Trash2, Plus, Loader2, ImageIcon, ScanText, RotateCcw, Bot } from "lucide-react";
import AccountSettings from "@/components/AccountSettings";
import VerifyBanner from "@/components/VerifyBanner";
import UsageMeter from "@/components/UsageMeter";
import { UserAvatar } from "@/components/UserAvatar";
import LibrarySidebar from "@/components/library/LibrarySidebar";
import { MoveToMenu, TagEditor } from "@/components/library/DocOrganize";
import { TagChip } from "@/components/library/TagChip";
import BotCreateDialog from "@/components/bots/BotCreateDialog";
import BotsDialog from "@/components/bots/BotsDialog";

const CATEGORY_LABELS = { legal: "Legal", research: "Research", textbook: "Textbook", manual: "Manual", financial: "Financial", medical: "Medical", business: "Business", other: "Other" };
const BUSY = (d) => d.status === "processing" || ["queued", "running"].includes(d.cover_status);

function Cover({ d }) {
  if (d.cover_job_id) return <img src={`${API}/media/${d.cover_job_id}/file?auth=${getToken()}`} alt="" data-testid={`doc-cover-${d.id}`} className="w-10 h-[52px] object-cover border border-border flex-shrink-0" />;
  return (
    <div data-testid={`doc-cover-placeholder-${d.id}`} className="w-10 h-[52px] border border-border bg-muted grid place-items-center flex-shrink-0">
      {["queued", "running"].includes(d.cover_status) ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : <FileText className="w-4 h-4 text-primary" strokeWidth={1.4} />}
    </div>
  );
}

function DocLength({ d, onRetry }) {
  if (d.status === "processing") {
    return (
      <span data-testid={`doc-ocr-progress-${d.id}`} className="inline-flex items-center gap-1.5 text-primary">
        <ScanText className="w-3.5 h-3.5 animate-pulse" /> Reading scan {d.ocr_pending ? `${d.ocr_done || 0}/${d.ocr_pending}` : ""}
      </span>
    );
  }
  if (d.status === "failed") {
    return (
      <button data-testid={`doc-ocr-retry-${d.id}`} onClick={() => onRetry(d.id)} title={d.error} className="inline-flex items-center gap-1 text-destructive underline underline-offset-2">
        <RotateCcw className="w-3.5 h-3.5" /> No text · retry
      </button>
    );
  }
  return <>{d.num_pages} {d.unit === "section" ? "sec." : "pp."}</>;
}

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [folderFilter, setFolderFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState([]);
  const [botDocs, setBotDocs] = useState(null);
  const [botsKey, setBotsKey] = useState(0);
  const fileRef = useRef(null);
  const pollTimer = useRef(null);

  const loadFolders = useCallback(() => api.get("/folders").then((r) => setFolders(r.data)).catch(() => {}), []);
  const load = useCallback(async () => {
    try {
      const [r, u] = await Promise.all([api.get("/documents"), api.get("/account/usage"), loadFolders()]);
      setDocs(r.data);
      setUsage(u.data);
      clearTimeout(pollTimer.current);
      if (r.data.some(BUSY)) pollTimer.current = setTimeout(load, 5000);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [loadFolders]);
  useEffect(() => { load(); return () => clearTimeout(pollTimer.current); }, [load]);

  useEffect(() => {
    if (params.get("billing") !== "done") return;
    api.get("/payments/subscription").finally(() => refresh());
    toast.success("Billing details updated");
    setParams({}, { replace: true });
  }, [params, setParams, refresh]);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let ok = 0;
    const targetFolder = folders.some((f) => f.id === folderFilter) ? folderFilter : null;
    for (const [idx, f] of files.entries()) {
      setProgress({ name: f.name, pct: 0, idx: idx + 1, total: files.length });
      try {
        const data = await uploadFile(f, (pct) => setProgress((p) => (p ? { ...p, pct } : p)));
        ok++;
        if (targetFolder) api.patch(`/documents/${data.id}`, { folder_id: targetFolder }).catch(() => {});
        if (data.status === "processing") toast.info(`${f.name}: scanned pages detected — reading them with OCR, this can take a few minutes`, { duration: 8000 });
      } catch (err) {
        const limit = [403, 413].includes(err.response?.status);
        toast.error(`${f.name}: ${errText(err, "Upload failed")}`, limit ? { duration: 10000, action: { label: "See plans", onClick: () => nav("/#pricing") } } : undefined);
      }
    }
    setProgress(null);
    if (ok) toast.success(`Uploaded ${ok} document${ok > 1 ? 's' : ''}`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const retryOcr = async (id) => {
    try { await api.post(`/documents/${id}/retry-ocr`); toast.success("Reading the scan again"); load(); }
    catch (err) { toast.error(errText(err, "Couldn't retry")); }
  };

  const missingCovers = docs.filter((d) => !d.cover_status || d.cover_status === "failed").length;
  const backfill = async () => {
    try {
      const { data } = await api.post("/documents/covers/backfill");
      toast.success(data.queued ? `Making ${data.queued} cover picture${data.queued > 1 ? "s" : ""}, about a minute` : "Every document already has a cover");
      load();
    } catch {
      toast.error("Couldn't start the covers");
    }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const openChat = (ids) => nav(`/app/chat/${(Array.isArray(ids) ? ids : [ids]).join(",")}`);

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Deleted");
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const moveDocs = async (ids, folderId) => {
    try {
      const { data } = await api.post("/documents/move", { ids, folder_id: folderId });
      const name = folderId ? folders.find((f) => f.id === folderId)?.name : "Unfiled";
      toast.success(`Moved ${data.moved} document${data.moved === 1 ? "" : "s"} to ${name}`);
      setDocs((ds) => ds.map((d) => (ids.includes(d.id) ? { ...d, folder_id: folderId } : d)));
      loadFolders();
    } catch (err) { toast.error(errText(err, "Couldn't move")); }
  };
  const onTagsSaved = (id, tags) => setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, tags } : d)));

  const allTags = useMemo(() => [...new Set(docs.flatMap((d) => d.tags || []))].sort(), [docs]);
  const currentFolder = folders.find((f) => f.id === folderFilter);
  const scoped = docs.filter((d) => folderFilter === "all" || (folderFilter === "none" ? !d.folder_id : d.folder_id === folderFilter))
    .filter((d) => tagFilter.every((t) => (d.tags || []).includes(t)));
  const visible = scoped.filter((d) => filter === "all" || d.category === filter);
  const selectedDocs = docs.filter((d) => selected.has(d.id));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-foreground text-background grid place-items-center font-serif-display text-lg font-black">D</div>
            <span className="font-serif-display text-xl tracking-tight">DocuMind</span>
          </Link>
          <div className="flex items-center gap-4">
            <span data-testid="dashboard-plan-badge" className={`font-mono-label px-2.5 py-1 border ${user?.plan && user.plan !== "free" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>
              {user?.plan && user.plan !== "free" ? `${user.plan} plan` : "Free plan"}
            </span>
            {(!user?.plan || user.plan === "free") && (
              <Link to="/#pricing" data-testid="dashboard-upgrade-link" className="text-sm text-primary hover:underline">Upgrade</Link>
            )}
            <UserAvatar name={user?.name} picture={user?.picture} testId="dashboard-user-avatar" />
            <span className="hidden sm:inline text-sm text-muted-foreground">{user?.email}</span>
            <BotsDialog refreshKey={botsKey} defaultOpen={params.get("bots") === "1"} />
            <AccountSettings />
            <Button variant="outline" size="sm" data-testid="dashboard-logout" onClick={() => { logout(); nav('/'); }}>Log out</Button>
          </div>
        </div>
      </header>
      <VerifyBanner />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <div className="font-mono-label text-muted-foreground mb-2">{currentFolder ? "Folder" : "Library"}</div>
            <h1 data-testid="library-heading" className="font-serif-display text-4xl lg:text-5xl tracking-tighter">
              {docs.length === 0 ? "Upload your first document." : currentFolder ? currentFolder.name : folderFilter === "none" ? "Unfiled documents." : `${docs.length} document${docs.length > 1 ? 's' : ''} in library.`}
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {missingCovers > 0 && (
              <Button data-testid="backfill-covers-btn" variant="outline" onClick={backfill} className="rounded-sm border-foreground">
                <ImageIcon className="w-4 h-4 mr-2" /> Make covers for {missingCovers} file{missingCovers > 1 ? "s" : ""}
              </Button>
            )}
            {selected.size > 0 && (
              <>
                <MoveToMenu folders={folders} testId="bulk-move" label={`Move ${selected.size}`} className="rounded-sm border border-border"
                  onMove={(fid) => { moveDocs([...selected], fid); setSelected(new Set()); }} />
                <Button data-testid="publish-bot-btn" variant="outline" onClick={() => setBotDocs(selectedDocs.slice(0, 5))} className="rounded-sm border-foreground">
                  <Bot className="w-4 h-4 mr-2" /> Publish chatbot
                </Button>
                <Button data-testid="chat-selected-btn" onClick={() => openChat([...selected])} className="rounded-sm bg-primary text-primary-foreground hover:bg-foreground btn-invert">
                  <MessageSquare className="w-4 h-4 mr-2" /> Chat with {selected.size} selected
                </Button>
              </>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" multiple onChange={onUpload} className="hidden" data-testid="upload-input" />
            <Button data-testid="upload-btn" onClick={() => fileRef.current?.click()} disabled={!!progress} className="rounded-sm bg-foreground text-background hover:bg-primary btn-invert min-w-[150px]">
              {progress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {progress ? `${progress.pct}%${progress.total > 1 ? ` · ${progress.idx}/${progress.total}` : ""}` : "Upload files"}
            </Button>
          </div>
        </div>

        {progress && (
          <div data-testid="upload-progress" className="mb-6 border border-border p-3">
            <div className="flex justify-between text-xs text-muted-foreground mb-1.5"><span className="truncate">Uploading {progress.name}</span><span>{progress.pct}%</span></div>
            <div className="h-1.5 bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${progress.pct}%` }} /></div>
          </div>
        )}

        <div className="mb-8"><UsageMeter usage={usage} /></div>

        {loading ? (
          <div className="font-mono-label text-muted-foreground">Loading…</div>
        ) : docs.length === 0 ? (
          <div data-testid="empty-dropzone" onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-border p-16 text-center cursor-pointer hover:bg-muted">
            <Plus strokeWidth={1} className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <div className="font-serif-display text-2xl mb-2">Drop documents here</div>
            <p className="text-sm text-muted-foreground">PDF, Word (.docx) or text files — scanned PDFs welcome. Up to {usage?.limits?.max_mb ?? 50} MB each.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[220px_1fr] gap-10">
            <LibrarySidebar folders={folders} docs={docs} folderFilter={folderFilter} setFolderFilter={setFolderFilter}
              tagFilter={tagFilter} setTagFilter={setTagFilter} onFoldersChanged={load} />
            <div className="min-w-0">
              <div className="flex flex-wrap gap-2 mb-5" data-testid="category-filters">
                {["all", ...Object.keys(CATEGORY_LABELS).filter((c) => scoped.some((d) => d.category === c))].map((c) => (
                  <button key={c} data-testid={`category-filter-${c}`} onClick={() => setFilter(c)}
                    className={`font-mono-label px-3 py-1.5 border ${filter === c ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}>
                    {c === "all" ? `All · ${scoped.length}` : `${CATEGORY_LABELS[c]} · ${scoped.filter((d) => d.category === c).length}`}
                  </button>
                ))}
              </div>
              {visible.length === 0 ? (
                <div data-testid="library-empty-filter" className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                  Nothing here yet. {currentFolder ? "Upload a file while this folder is open, or move documents in with “Move to”." : "Try another filter."}
                </div>
              ) : (
              <div className="border-t border-l border-border">
                <div className="grid grid-cols-12 bg-muted border-r border-b border-border font-mono-label text-muted-foreground">
                  <div className="col-span-1 p-4"></div>
                  <div className="col-span-4 p-4">Document</div>
                  <div className="col-span-2 p-4">Category</div>
                  <div className="col-span-1 p-4">Length</div>
                  <div className="col-span-1 p-4">Uploaded</div>
                  <div className="col-span-3 p-4"></div>
                </div>
                {visible.map((d) => (
                  <div key={d.id} data-testid={`doc-row-${d.id}`} className="grid grid-cols-12 border-r border-b border-border hover:bg-muted group">
                    <div className="col-span-1 p-4 flex items-center">
                      <Checkbox data-testid={`doc-check-${d.id}`} checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} />
                    </div>
                    <div className="col-span-4 p-3 flex items-center gap-3 cursor-pointer min-w-0" onClick={() => openChat(d.id)}>
                      <Cover d={d} />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.filename}</div>
                        {(d.tags?.length > 0 || (folderFilter === "all" && d.folder_id)) && (
                          <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`doc-tags-row-${d.id}`}>
                            {folderFilter === "all" && d.folder_id && <span className="text-[11px] text-muted-foreground mr-1">{folders.find((f) => f.id === d.folder_id)?.name}</span>}
                            {(d.tags || []).map((t) => <TagChip key={t} name={t} testId={`doc-tag-${d.id}-${t}`} onClick={(e) => { e.stopPropagation(); setTagFilter(tagFilter.includes(t) ? tagFilter : [...tagFilter, t]); }} />)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2 p-4 flex items-center">
                      <span data-testid={`doc-category-${d.id}`} className="font-mono-label border border-border px-2 py-1">{CATEGORY_LABELS[d.category] || "Other"}</span>
                    </div>
                    <div className="col-span-1 p-4 flex items-center text-sm text-muted-foreground"><DocLength d={d} onRetry={retryOcr} /></div>
                    <div className="col-span-1 p-4 flex items-center text-sm text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</div>
                    <div className="col-span-3 p-2 flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`doc-chat-${d.id}`} onClick={() => openChat(d.id)} title="Chat"><MessageSquare className="w-4 h-4" /></Button>
                      <MoveToMenu folders={folders} currentFolderId={d.folder_id} testId={`doc-move-${d.id}`} className="h-8 w-8 p-0" onMove={(fid) => moveDocs([d.id], fid)} />
                      <TagEditor doc={d} allTags={allTags} onSaved={onTagsSaved} />
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`doc-bot-${d.id}`} onClick={() => setBotDocs([d])} title="Publish as chatbot" disabled={d.status === "processing"}><Bot className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`doc-delete-${d.id}`} onClick={() => del(d.id)} title="Delete"><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>
        )}
      </main>
      <BotCreateDialog open={!!botDocs} onOpenChange={(o) => { if (!o) setBotDocs(null); }} docs={botDocs || []} onCreated={() => setBotsKey((k) => k + 1)} />
    </div>
  );
}
